import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Grid,
  Card,
  Chip
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  WhatsApp as WhatsAppIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime as TimeIcon,
  MonetizationOn as MoneyIcon,
  Security as SecurityIcon,
  Support as SupportIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../utils/LanguageContext';
import Layout from '../components/layout/Layout';
import contactInfo from '../config/contact';
import QuickOrderSection from '../components/home/QuickOrderSection';

const FaqPage: React.FC = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [expanded, setExpanded] = useState<string | false>('general1');

  const whatsappUrl = `https://wa.me/${contactInfo.whatsapp.number}?text=${encodeURIComponent(contactInfo.whatsapp.message[language as keyof typeof contactInfo.whatsapp.message])}`;

  const scrollToQuickOrder = () => {
    window.location.href = '/#quick-order-form';
  };

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const faqCategories = [
    {
      id: 'general',
      title: 'General Questions',
      icon: <SupportIcon />,
      color: '#4A90E2',
      faqs: [
        {
          id: 'general1',
          question: 'What services does GamiFly Tech provide?',
          answer: 'We provide comprehensive software development services including mobile app development (iOS & Android), web development, cloud services, UI/UX design, and technical support. We specialize in creating custom solutions tailored to your specific business needs.'
        },
        {
          id: 'general2',
          question: 'Do you work with businesses outside Hong Kong?',
          answer: 'Yes! While we\'re based in Hong Kong, we work with clients globally. Our team is fluent in English, Cantonese, and Mandarin, making communication seamless regardless of your location.'
        },
        {
          id: 'general3',
          question: 'What makes GamiFly Tech different from other development companies?',
          answer: 'We focus on fast delivery (demos within 24 hours), transparent pricing with no hidden costs, local Hong Kong-based team for easy communication, and ongoing support even after project completion. We believe in building long-term partnerships with our clients.'
        },
        {
          id: 'general4',
          question: 'Can you help with both small projects and large enterprise solutions?',
          answer: 'Absolutely! We work with startups needing simple mobile apps to large enterprises requiring complex, scalable solutions. Our team adapts to projects of any size and complexity.'
        }
      ]
    },
    {
      id: 'pricing',
      title: 'Pricing & Payment',
      icon: <MoneyIcon />,
      color: '#66BB6A',
      faqs: [
        {
          id: 'pricing1',
          question: 'How much do your services cost?',
          answer: 'Our pricing varies based on project complexity and requirements. Web development starts from HK$8,000, mobile apps from HK$15,000, and UI/UX design from HK$5,000. Contact us for a detailed quote tailored to your specific needs.'
        },
        {
          id: 'pricing2',
          question: 'Do you offer fixed-price or hourly billing?',
          answer: 'We offer both options depending on your preference and project type. For well-defined projects, we provide fixed-price quotes. For ongoing development or projects with evolving requirements, we offer competitive hourly rates.'
        },
        {
          id: 'pricing3',
          question: 'Are there any hidden costs?',
          answer: 'No hidden costs ever! We provide transparent, upfront pricing that includes all development work, testing, and initial deployment. Any additional costs (like third-party services or hosting) are clearly discussed and approved by you beforehand.'
        },
        {
          id: 'pricing4',
          question: 'What payment methods do you accept?',
          answer: 'We accept bank transfers, PayPal, and major credit cards. For larger projects, we typically work with milestone-based payments to ensure both parties are comfortable throughout the development process.'
        }
      ]
    },
    {
      id: 'timeline',
      title: 'Timeline & Process',
      icon: <TimeIcon />,
      color: '#FF7043',
      faqs: [
        {
          id: 'timeline1',
          question: 'How quickly can you deliver a demo?',
          answer: 'We pride ourselves on fast turnaround times. For most projects, you\'ll receive an initial demo or prototype within 24 hours of providing your requirements. This helps you visualize the solution before committing to the full development.'
        },
        {
          id: 'timeline2',
          question: 'What is your typical project timeline?',
          answer: 'Timeline depends on project complexity: Simple websites (1-3 weeks), Mobile apps (2-8 weeks), Complex web applications (4-12 weeks), Enterprise solutions (3-6 months). We provide detailed timelines with milestones for every project.'
        },
        {
          id: 'timeline3',
          question: 'Can you work with tight deadlines?',
          answer: 'Yes! We understand business urgency and can often accommodate tight deadlines. We\'ll assess your requirements and provide an honest timeline. If needed, we can allocate additional resources to meet your launch date.'
        },
        {
          id: 'timeline4',
          question: 'How do you handle project updates and communication?',
          answer: 'We provide regular updates throughout development via your preferred communication method (WhatsApp, email, video calls). You\'ll have access to a project dashboard showing real-time progress and can request updates anytime.'
        }
      ]
    },
    {
      id: 'technical',
      title: 'Technical Questions',
      icon: <SecurityIcon />,
      color: '#AB47BC',
      faqs: [
        {
          id: 'technical1',
          question: 'What technologies do you use?',
          answer: 'We use modern, industry-standard technologies including React/Vue.js for web, React Native/Flutter for mobile, Node.js/Python for backend, AWS/Google Cloud for hosting, and more. We choose the best tech stack for each project\'s specific needs.'
        },
        {
          id: 'technical2',
          question: 'Do you provide ongoing maintenance and support?',
          answer: 'Yes! We offer comprehensive maintenance packages starting from HK$3,000/month, including bug fixes, security updates, performance monitoring, and feature enhancements. We believe in supporting your success long-term.'
        },
        {
          id: 'technical3',
          question: 'Is my data and intellectual property secure?',
          answer: 'Absolutely. We implement industry-standard security practices, sign NDAs for all projects, and follow strict data protection protocols. Your code and data remain completely confidential and belong entirely to you.'
        },
        {
          id: 'technical4',
          question: 'Can you integrate with existing systems?',
          answer: 'Yes, we specialize in integrations! Whether it\'s connecting to your existing CRM, payment systems, databases, or third-party APIs, we ensure seamless integration with your current business tools and workflows.'
        }
      ]
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <Box
        sx={{
          width: '100vw',
          position: 'relative',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
          py: { xs: 10, md: 15 },
          background: 'linear-gradient(135deg, rgba(18,18,18,0.95) 0%, rgba(26,32,48,0.95) 100%)',
          overflow: 'hidden',
          mb: 8,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url(/pattern-bg.png) repeat',
            opacity: 0.1,
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box textAlign="center">
            <Typography
              variant="h1"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '4rem' },
                background: 'linear-gradient(45deg, #4A90E2 30%, #5C6BC0 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 3,
              }}
            >
              {t('faq.title')}
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{
                maxWidth: 800,
                mx: 'auto',
                mb: 6,
                lineHeight: 1.6,
                px: { xs: 2, md: 0 },
              }}
            >
              {t('faq.subtitle')}
            </Typography>

            {/* Search Bar Placeholder - You can implement actual search functionality later */}
            <Box
              sx={{
                maxWidth: 600,
                mx: 'auto',
                px: { xs: 2, md: 0 },
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={scrollToQuickOrder}
                fullWidth
                sx={{
                  py: 2,
                  background: 'linear-gradient(45deg, #4A90E2 30%, #5C6BC0 90%)',
                  fontSize: '1.1rem',
                }}
              >
                {t('faq.contactSection.getQuote')}
              </Button>
              <Button
                variant="outlined"
                size="large"
                component="a"
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<WhatsAppIcon />}
                fullWidth
                sx={{
                  py: 2,
                  borderColor: '#25D366',
                  color: '#25D366',
                  fontSize: '1.1rem',
                  '&:hover': {
                    borderColor: '#25D366',
                    backgroundColor: '#25D36620',
                  }
                }}
              >
                {t('faq.contactSection.whatsapp')}
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">

        {/* FAQ Categories */}
        {faqCategories.map((category) => (
          <Box key={category.id} sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  background: `${category.color}20`,
                  color: category.color,
                  mr: 2,
                }}
              >
                {category.icon}
              </Box>
              <Typography variant="h4" component="h2" sx={{ fontWeight: 600 }}>
                {t(`faq.categories.${category.id}.title`)}
              </Typography>
              <Chip
                label={`${category.faqs.length} ${t(`faq.categories.${category.id}.questions`)}`}
                size="small"
                sx={{
                  ml: 2,
                  bgcolor: `${category.color}20`,
                  color: category.color,
                }}
              />
            </Box>

            {category.faqs.map((faq) => (
              <Accordion
                key={faq.id}
                expanded={expanded === faq.id}
                onChange={handleChange(faq.id)}
                sx={{
                  mb: 2,
                  background: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px !important',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    boxShadow: `0 8px 32px ${category.color}30`,
                    border: `1px solid ${category.color}50`,
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: category.color }} />}
                  sx={{
                    borderRadius: '12px',
                    '& .MuiAccordionSummary-content': {
                      my: 2,
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t(`faq.questions.${faq.id}.question`)}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ lineHeight: 1.7 }}
                  >
                    {t(`faq.questions.${faq.id}.answer`)}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ))}

        {/* Quick Order Section */}
        <Box id="quick-order-form">
          <QuickOrderSection />
        </Box>
      </Container>
    </Layout>
  );
};

export default FaqPage; 